import React from "react";
import classNames from "classnames";
import { Switch } from "@headlessui/react";
import PropTypes from "prop-types";

/**
 * Enhanced Toggle component with uniform sizing and styling
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.value=false] - Current toggle state
 * @param {Function} props.onChange - Function called when toggle state changes
 * @param {string} [props.size="md"] - Size variant (sm, md, lg)
 * @param {string} [props.variant="default"] - Style variant (default, success, warning, danger)
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled=false] - Whether the toggle is disabled
 * @param {string} [props.label] - Optional label text
 * @param {string} [props.description] - Optional description text
 * @param {boolean} [props.showLabel=true] - Whether to show the label
 */
export function EnhancedToggle({
  value = false,
  onChange,
  size = "md",
  variant = "default",
  className = "",
  disabled = false,
  label,
  description,
  showLabel = true
}) {
  // Size variants
  const sizeClasses = {
    sm: {
      switch: "h-5 w-9",
      thumb: "h-4 w-4",
      translate: value ? "translate-x-4" : "translate-x-0",
      label: "text-sm",
      description: "text-xs"
    },
    md: {
      switch: "h-6 w-11",
      thumb: "h-5 w-5",
      translate: value ? "translate-x-5" : "translate-x-0",
      label: "text-sm",
      description: "text-xs"
    },
    lg: {
      switch: "h-7 w-12",
      thumb: "h-6 w-6",
      translate: value ? "translate-x-5" : "translate-x-0",
      label: "text-base",
      description: "text-sm"
    }
  };

  // Variant styles for active state
  const variantClasses = {
    default: "bg-indigo-600 focus:ring-indigo-500",
    success: "bg-green-600 focus:ring-green-500",
    warning: "bg-yellow-600 focus:ring-yellow-500",
    danger: "bg-red-600 focus:ring-red-500"
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  const toggleComponent = (
    <Switch
      checked={value}
      onChange={onChange}
      disabled={disabled}
      className={classNames(
        "relative inline-flex shrink-0 border-2 border-transparent rounded-full cursor-pointer",
        "transition-colors ease-in-out duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        currentSize.switch,
        value ? currentVariant : "bg-gray-200 dark:bg-slate-600 focus:ring-gray-500",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      <span className="sr-only">{label || "Toggle setting"}</span>
      <span
        aria-hidden="true"
        className={classNames(
          "pointer-events-none inline-block rounded-full bg-white shadow transform ring-0",
          "transition ease-in-out duration-200",
          currentSize.thumb,
          currentSize.translate
        )}
      />
    </Switch>
  );

  // If no label provided or showLabel is false, return just the toggle
  if (!label || !showLabel) {
    return toggleComponent;
  }

  // Return toggle with label and description
  return (
    <div className="flex items-start">
      <div className="flex h-5 items-center">
        {toggleComponent}
      </div>
      <div className="ml-3 text-sm">
        <label className={classNames("font-medium text-gray-700 dark:text-slate-200", currentSize.label)}>
          {label}
        </label>
        {description && (
          <p className={classNames("text-gray-500 dark:text-slate-400", currentSize.description)}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

EnhancedToggle.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  variant: PropTypes.oneOf(["default", "success", "warning", "danger"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  showLabel: PropTypes.bool,
};

export default EnhancedToggle;
